import {
  Button,
  Flex,
  Heading,
  Input,
  InputTypes,
  showAlertModalError,
} from '@conteg/ui';
import Notification from 'components/notification/notification';
import { DateTime } from 'luxon';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  StorageUnitDocument,
  UpdateReservationDurationErrorCodes,
  useUpdateReservationDurationMutation,
} from 'types/generated/graphql';
import { resolveGqlErrorMessageKey } from 'utils/errors/resolve-gql-error-message-key';
import { resolveTwoFactorErrorKey } from 'utils/errors/resolve-two-factor-error-key';
import { useInvalidateQueries } from 'utils/hooks/use-ivalidate-queries';

type FormInputs = {
  date: string;
  time: string;
};

export type ChangeReservationDurationMode = 'bidirectional' | 'shortenOnly';

const getMaxTime = ({
  selectedDate,
  maxDate,
  maxTime,
}: {
  selectedDate: string;
  maxDate: string;
  maxTime: string;
}) => {
  if (selectedDate === maxDate) {
    return maxTime;
  }
  return;
};

// Backend emits these codes in GraphQL `extensions.errorCode` for the
// `updateReservationDuration` mutation. The enum is generated from
// schema.graphql — adding a key here requires a corresponding backend change.
const errorCodeToI18nKey: Record<UpdateReservationDurationErrorCodes, string> =
  {
    [UpdateReservationDurationErrorCodes.UpdateReservationDurationOverlap]:
      'Page.StorageUnit.Actions.UpdateReservationDuration.Error.Overlap',
    [UpdateReservationDurationErrorCodes.UpdateReservationDurationCapExceeded]:
      'Page.StorageUnit.Actions.UpdateReservationDuration.Error.CapExceeded',
    [UpdateReservationDurationErrorCodes.UpdateReservationDurationToBeforeFrom]:
      'Page.StorageUnit.Actions.UpdateReservationDuration.Error.ToBeforeFrom',
    [UpdateReservationDurationErrorCodes.UpdateReservationDurationReservationExpired]:
      'Page.StorageUnit.Actions.UpdateReservationDuration.Error.ReservationExpired',
    [UpdateReservationDurationErrorCodes.UpdateReservationDurationToInThePast]:
      'Page.StorageUnit.Actions.UpdateReservationDuration.Error.ToInThePast',
  };

const getI18nKeys = (mode: ChangeReservationDurationMode) =>
  mode === 'bidirectional'
    ? {
        heading: 'Page.StorageUnit.Actions.ChangeReservationDuration.Heading',
        success: 'Page.StorageUnit.Actions.ChangeReservationDuration.Success',
        error: 'Page.StorageUnit.Actions.ChangeReservationDuration.Error',
      }
    : {
        heading: 'Page.StorageUnit.Actions.UpdateReservationDuration.Heading',
        success: 'Page.StorageUnit.Actions.UpdateReservationDuration.Success',
        error: 'Page.StorageUnit.Actions.UpdateReservationDuration.Error',
      };

const ChangeReservationDuration = ({
  reservationId,
  from,
  to,
  mode = 'shortenOnly',
}: {
  reservationId: string;
  from: string;
  to: string;
  mode?: ChangeReservationDurationMode;
}) => {
  const { t } = useTranslation();
  const invalidate = useInvalidateQueries();
  const [formNotice, setFormNotice] = useState<string>('');

  const isBidirectional = mode === 'bidirectional';
  const i18n = getI18nKeys(mode);

  const now = DateTime.now();
  const reservationEnd = DateTime.fromISO(to);

  const upperBound = isBidirectional ? now.plus({ days: 1 }) : reservationEnd;

  // Backend rejects To values in the past, so clamp the picker's lower bound
  // to today even when the reservation started earlier.
  const reservationStart = DateTime.fromISO(from);
  const lowerBound = reservationStart > now ? reservationStart : now;

  const minDate = lowerBound.toISODate() ?? '';
  const maxDate = upperBound.toISODate() ?? '';
  const lastPossibleReservationTime = upperBound.toFormat('HH:mm');

  // For expired reservations in bidirectional mode the original `to` is in the
  // past; opening the picker there forces the user to navigate forward, so
  // default to "now" when the reservation has already ended.
  const hasFutureEnd = reservationEnd > now;
  const defaultDateTime =
    isBidirectional && hasFutureEnd ? reservationEnd : now;

  const formActions = useForm<FormInputs>({
    defaultValues: {
      date: defaultDateTime.toISODate() ?? DateTime.now().toISODate(),
      time: defaultDateTime.toFormat('HH:mm'),
    },
  });
  const { handleSubmit, reset, register, watch } = formActions;

  const date = watch('date');
  const time = watch('time');

  const { mutate: updateReservationDuration, isPending } =
    useUpdateReservationDurationMutation();

  const handleUpdateReservationDuration = () => {
    setFormNotice('');
    updateReservationDuration(
      {
        reservationId,
        to: DateTime.fromISO(`${date}T${time}`).toUTC().toString(),
      },
      {
        onSuccess: () => {
          setFormNotice(i18n.success);
          invalidate([StorageUnitDocument]);
          reset();
        },
        onError: (error) => {
          // The two-factor refusal comes from the gateway, so it is not one of the reservation API's
          // duration error codes and has to be picked out before that table is consulted.
          const messageKey = resolveTwoFactorErrorKey(
            error,
            resolveGqlErrorMessageKey(error, errorCodeToI18nKey, i18n.error)
          );
          showAlertModalError(t(messageKey), error);
        },
      }
    );
  };

  return (
    <FormProvider {...formActions}>
      <form onSubmit={handleSubmit(handleUpdateReservationDuration)}>
        <Flex flexDirection="column" alignItems="center" gap="5rem">
          <Heading title={t(i18n.heading)} variant="h2" />
          {formNotice && (
            <Notification
              testId="update-reservation-duration-notification"
              message={formNotice}
            />
          )}
          <Flex gap="4rem">
            <Input
              testId="input-date"
              placeholder={t('Date')}
              label={t('Date')}
              type={InputTypes.DATE}
              min={minDate}
              max={maxDate}
              {...register('date', { required: true })}
            />
            <Input
              testId="input-time"
              placeholder={t('Time')}
              label={t('Time')}
              type={InputTypes.TIME}
              max={getMaxTime({
                selectedDate: date,
                maxDate,
                maxTime: lastPossibleReservationTime,
              })}
              {...register('time', { required: true })}
            />
          </Flex>
          <Flex alignItems="center">
            <Button
              testId="update-reservation-duration-confirmation-button"
              variant="primary"
              size="xl"
              title={t('Page.Reservation.Form.Button')}
              disabled={isPending}
              type="submit"
            />
          </Flex>
        </Flex>
      </form>
    </FormProvider>
  );
};

export default ChangeReservationDuration;
